<template>
    <div
        class="all-widgets"
        v-if="loadedWidgets"
        id="drag"
        @contextmenu="disableContextMenu($event)"
    >
        <div
            v-for="(item, index) in loadedWidgets"
            :key="`item-${index}`"
            v-show="item.active"
        >
            <DigitalModelWidget
                v-if="item.id === 'digital-model'"
                :data="data"
                :active="item.active"
                :link="item.link"
                :mode="mode"
                :standalone="startedWidget === 'digital-model'"
                :defaultWidgetTitle="defaultWidgetTitle"
                :widgetResolutions="widgetResolutions"
                :loadedWidgets="loadedWidgets"
            />
            <CutawayWidget
                v-else-if="item.id === 'cutaway'"
                :data="data"
                :active="item.active"
                :link="item.link"
                :standalone="startedWidget === 'cutaway'"
                :isOpenedSidebar="isOpenedSidebar"
                :loadedWidgets="loadedWidgets"
            />
            <DroneWidget
                v-else-if="(item.id === 'drone-view' || item.id === 'drone-floor-view') && item.active"
                :data="data"
                :widget="startedWidget"
                :active="item.active"
                :link="item.link"
                :standalone="startedWidget === 'drone-view' || startedWidget === 'drone-floor-view'"
            />
            <VirtualTourWidget
                v-else-if="item.id === 'virtual-tour'"
                :data="data"
                :active="item.active"
                :link="item.link"
                :standalone="startedWidget === 'virtual-tour'"
            />
            <PlotWidget
                v-else-if="item.id === 'plot'"
                :data="data"
                :active="item.active"
                :link="item.link"
                :standalone="startedWidget === 'plot'"
            />
            <PlanWidget
                v-else-if="item.id === '2d-plan' && item.active"
                :data="data"
                :active="item.active"
                :link="item.link"
                :standalone="startedWidget === '2d-plan'"
            />
            <ThreeDWidget
                v-else-if="item.id === '3d-plan'"
                :data="data"
                :active="item.active"
                :link="item.link"
                :standalone="startedWidget === '3d-plan'"
            />
            <VideoWidget
                v-else-if="item.id === 'animation'"
                :data="data"
                :active="item.active"
                :link="item.link"
                :standalone="startedWidget === 'animation'"
            />
            <CarouselWidget
                v-else-if="item.id == 'carousel' || item.id == 'carousel-lot'"
                :data="data"
                :active="item.active"
                :link="item.link"
                :standalone="startedWidget === 'carousel' || startedWidget === 'carousel-lot'"
                :isOpenedSidebar="isOpenedSidebar"
            />
            <MapWidget
                v-else-if="item.id === 'map-view'"
                :data="data"
                :active="item.active"
                :link="item.link"
                :standalone="startedWidget === 'map-view'"
            />
            <VirtualTourModelsWidget
                v-else-if="item.id === 'models'"
                :data="data"
                :active="item.active"
                :link="item.link"
                :standalone="startedWidget === 'models'"
            />
            <PerspectiveWidget
                v-else-if="item.id === 'perspective' && item.active"
                :data="data"
                :active="item.active"
                :link="item.link"
                :standalone="startedWidget === 'perspective'"
            />
            <DistrictVideoWidget
                v-else-if="item.id === 'district-video'"
                :data="data"
                :active="item.active"
                :link="item.link"
                :standalone="startedWidget === 'district-video'"
            />
            <ConfigurableTourWidget
                v-else-if="item.id === 'configurable-tour'"
                :data="data"
                :active="item.active"
                :link="item.link"
                :standalone="startedWidget === 'configurable-tour'"
            />
            <IframeWidget
                v-else-if="iframeWidgets.includes(item.id)"
                :url="item.link"
                :widgetId="item.id"
            />
            <PlotGridWidget
                v-else-if="item.id === 'plot-grid'"
                :data="data"
                :isOpenedSidebar="isOpenedSidebar"
                :active="item.active"
                :standalone="startedWidget === 'plot-grid'"
                :link="item.link"
                :isMobile="isMobile"
            />
            <DocumentationWidget
                v-else-if="item.id === 'documentation'"
                :data="data"
                :active="item.active"
                :standalone="startedWidget === 'documentation'"
                :link="item.link"
            />
            <AugmentedRealityWidget
                v-else-if="item.id === 'augmented-reality'"
                :active="item.active"
                :arUrl="data.arUrl"
            />
            <OneForAllWidget v-else-if="item.id === 'one-for-all'"/>
            <div 
                :style="[isAlternativeMode ? {position:'relative', top:'80px'} : '']" 
                v-else
            >
                Nothing suits....
            </div>
        </div>
        <Mention
            v-if="isMentionVisible && !isFrontless"
            :message="mentionMessage"
            :color="mentionColor"
        />
        <v-snackbar
            v-model="snackbarModel"
            app
            absolute
            :style="{
                top: '-2rem',
                zIndex: '999'
            }"
            class="text-white"
        >
            {{ snackbar ? snackbar.message : null }}
        </v-snackbar>
    </div>
</template>

<script>
    import _ from 'lodash';
    import { EventBus } from '@/event-bus';
    import Http from '@/api/data';
    import { mapActions, mapGetters } from 'vuex';

    import DigitalModelWidget from '@/views/DigitalModelWidget';
    import CutawayWidget from '@/views/CutawayWidget';
    import DroneWidget from '@/views/DroneWidget';
    import VirtualTourWidget from '@/views/VirtualTourWidget';
    import PlotWidget from '@/views/PlotWidget';
    import PlanWidget from '@/views/PlanWidget';
    import ThreeDWidget from '@/views/ThreeDWidget';
    import VideoWidget from '@/views/VideoWidget';
    import CarouselWidget from '@/views/CarouselWidget';
    import MapWidget from '@/views/MapWidget';
    import VirtualTourModelsWidget from '@/views/VirtualTourModelsWidget';
    import PerspectiveWidget from '@/views/PerspectiveWidget';
    import DistrictVideoWidget from '@/views/DistrictVideoWidget';
    import ConfigurableTourWidget from '@/views/ConfigurableTourWidget';
    import IframeWidget from '@/views/IframeWidget';
    import PlotGridWidget from '@/views/PlotGrid';
    import DocumentationWidget from '@/views/DocumentationWidget';
    import AugmentedRealityWidget from "@/views/AugmentedRealityWidget";
    import OneForAllWidget from "@/views/OneForAllWidget";

    import Mention from '@/components/Mention';

    export default {
        components: {
            AugmentedRealityWidget,
            DigitalModelWidget,
            CutawayWidget,
            DroneWidget,
            VirtualTourWidget,
            PlotWidget,
            PlanWidget,
            ThreeDWidget,
            VideoWidget,
            CarouselWidget,
            MapWidget,
            VirtualTourModelsWidget,
            PerspectiveWidget,
            DistrictVideoWidget,
            ConfigurableTourWidget,
            IframeWidget,
            PlotGridWidget,
            DocumentationWidget,
            OneForAllWidget,
            Mention
        },
        props: [
            'data',
            'loadedWidgets',
            'mode',
            'widgetResolutions',
            'defaultWidgetTitle',
            'isOpenedSidebar',
            'isMobile',
            'showSnackbbar',
            'defaultState'
        ],
        data: () => ({
            isMentionVisible: false,
            nonContractualWidgets: [
                '3d-plan',
                'general',
                'ground-plan',
                'pedestrian',
                'aerial',
                'cutaway',
                'carousel',
                'plot'
            ],
            iframeWidgets: [
                'sunshine',
                'cityscan'
            ],
            darkWidget: [],
            themeColor: {
                default: 'black',
                light: 'white'
            },
            mentionColor: null,
            mentionMessage: null,
            snackbarModel: false
        }),
        computed: {
            ...mapGetters([
                'isOfflineMode',
                'isOnline',
                'isReviewUse',
                'language',
                'logos',
                'protocol',
                'programInfo',
                'snackbar',
                'startedWidget',
                'widget',
                'isFrontless',
                'isAlternativeMode'
            ]),
            ...mapGetters('widget', [
                'requestedScene'
            ]),
            hasSharedFavlist() {
                return this.$route.query.favlist ? true : false;
            },
            apiUrl() {
                return this.protocol + (this.isReviewUse ? process.env.API_LIST.REVIEW : process.env.API_LIST.FO);
            }
        },
        mounted() {
            this.mentionColor = this.themeColor.default;
            if (this.startedWidget === 'program')
                this.render();

            EventBus.$on('program_SetActive', (item, subitem) => {
                EventBus.$emit('updateDefaultSidebar');
                let widgetList =  this.$props.data.levels || this.$props.data.widgetList;
                let currentWidget = widgetList.filter(widget => widget.id === item.id);
                let activeHotspot = null;
                if (currentWidget.length)
                    activeHotspot = currentWidget[0].hotspots ? currentWidget[0].hotspots.filter(hotspot => hotspot.active)[0] : null;
                this.setWidgetScene(null);
                if (activeHotspot)
                    item.active = false;

                this.resetActiveHotspots(widgetList);
                this.setActive(item, subitem);
            });
            EventBus.$on('configurableTour_setActive', (widget, configurableTour) => {
                    let localData = _.cloneDeep(this.data);
                    let widgetList = this.startedWidget === 'program' ? localData.widgetList : localData.levels;
                    this.resetActiveHotspots(widgetList);
                    let currentWidget = widgetList.filter(el => el.id === widget.id)[0];
                    currentWidget.configurableTours.forEach(el => el.active = false);
                    currentWidget.active = true;
                    let configurableTourWidget = currentWidget.configurableTours.filter(el => el.id === configurableTour.id);
                    if (configurableTourWidget.length)
                        configurableTourWidget[0].active = true;
                    
                    if (!widget.active) {
                        widgetList.filter(el => el.active).forEach(el => (el.active = false));
                        widgetList.filter(el => el.id === widget.id)[0].active = true;
                    }
                    EventBus.$emit("updateSidebar", localData, true);
                    EventBus.$emit("updateLoadedWidgets", {
                        id: widget.id,
                        link: configurableTour.link,
                        active: true,
                        widgetType: widget.widgetType
                    });    
            });

            EventBus.$on('hotspot_setActive', (widget, hotspot) => {
                if (hotspot.scene) {
                    this.setWidgetScene(hotspot.scene);

                    let localData = _.cloneDeep(this.data);
                    let widgetList = this.startedWidget === 'program' ? localData.widgetList : localData.levels;
                    this.resetActiveHotspots(widgetList);
                    let currentWidget = widgetList.filter(el => el.id === widget.id)[0];
                    currentWidget.hotspots.forEach(el => el.active = false);
                    currentWidget.active = true;
                    let hotspotWidget = currentWidget.hotspots.filter(el => el.id === hotspot.id);
                    if (hotspotWidget.length)
                        hotspotWidget[0].active = true;

                    EventBus.$emit("updateLoadedWidgets", {
                        id: widget.id,
                        link: widget.link,
                        active: true,
                        widgetType: widget.id
                    });

                    EventBus.$emit("updateSidebar", localData, false);
                } else
                    this.setActiveHotspot(widget, hotspot);
            });
        },
        methods: {
            ...mapActions([
                'setLogo',
                'setLoadingBackground',
                'setProgramInfo',
                'setSnackbar'
            ]),
            ...mapActions('widget', [
                'setWidgetScene'
            ]),
            resetActiveHotspots(widgetList) {
                widgetList.forEach(el => {
                    el.active = false;
                    if (el.hotspots)
                        el.hotspots.forEach(hotspot => hotspot.active = false);
                });
            },
            render() {
                let defaultLoadedWidget = {};
                let urlLink = this.apiUrl + "/request-data/program" + window.location.search;
                if (this.language && !urlLink.includes('&language'))
                    urlLink += '&language=' + this.language;
                Http.getData(urlLink).then(payload => {
                    if (payload) {
                        if (payload.program)
                            payload.heading = _.cloneDeep(payload.program);

                        if (payload.language)
                            this.$i18n.locale = payload.language;

                        if (!this.isOnline && this.isOfflineMode)
                            payload.items.forEach(item => {
                                if (item.id === "general") item.active = true;
                                else item.active = false;
                            });

                        let widgetList = [];
                        payload.type = "program";
                        payload.items.forEach(el => {
                            if (el.submenu) {
                                let activeItem = el.submenu.filter(
                                    item => !!item.active
                                );
                                if (activeItem.length) {
                                    this.$set(el, "loaded", true);
                                    this.$set(el, "active", true);
                                    this.$set(el, "link", activeItem[0].link);
                                    defaultLoadedWidget = {
                                        id: el.widgetType ? el.widgetType : el.id,
                                        link: el.link + window.location.search,
                                        active: true,
                                        widgetType: el.id
                                    };
                                }
                            } else {
                                // if (el.link) {
                                //     let currentWidgetUrl = new URL(el.link);
                                //     currentWidgetUrl.searchParams.delete('id');
                                //     currentWidgetUrl.searchParams.delete('key');
                                //     currentWidgetUrl.searchParams.delete('mode');
                                //     currentWidgetUrl.searchParams.delete('language');
                                //     el.link = currentWidgetUrl.href + window.location.search;
                                // }

                                if (el.id == 'general' && this.loaderBackground == null)
                                    this.getFirstGeneralViewPreview(el.link);

                                if (el.active) {
                                    this.$set(el, "loaded", true);
                                    defaultLoadedWidget = {
                                        id: el.widgetType ? el.widgetType : el.id,
                                        link: el.link,
                                        active: true,
                                        widgetType: el.id
                                    };
                                }
                            }
                            let widgetLink = el.link;
                            if (widgetLink) {
                                if (this.language && !widgetLink.includes('&language'))
                                    widgetLink += '&language=' + this.language;
                                widgetList.push({
                                    id: el.id,
                                    text: el.text,
                                    link: widgetLink,
                                    active: false
                                });
                            }
                        });

                        if (this.hasSharedFavlist) {
                            payload.items.forEach(item => {
                                item.active = ['cutaway', 'plot-grid'].includes(item.id);
                            });
                            defaultLoadedWidget = {
                                'id': 'plot-grid',
                                'link': this.apiUrl + '/request-data/cutaway-view' + window.location.search,
                                'active': true,
                                'widgetType': 'plot-grid'
                            }
                        }

                        payload.widgetList = _.cloneDeep(payload.items);
                        EventBus.$emit('setWidgetList', widgetList);
                        EventBus.$emit('setSidebarData', payload);
                        EventBus.$emit('updateLoadedWidgets', defaultLoadedWidget);
                        EventBus.$emit('setDefaultState', _.cloneDeep(payload)); //? Peut être amené à changer.

                        // Persist Program logo into store
                        if (!this.logos || (payload.program.logo && (this.logos && !('program' in this.logos))))
                            this.setLogo({
                                'type': 'program',
                                'src': payload.program.logo
                            });

                        // Persist promoter logo into store
                        if (!this.logos  || (payload.promoter.logo && (this.logos && !('promoter' in this.logos))))
                            this.setLogo({
                                'type': 'promoter',
                                'src': payload.promoter.logo
                            });

                        // Persist program info into store
                        if (!this.programInfoLink && 'program' in payload) {
                            if (payload.program.details)
                                Http.getData(payload.program.details).then(programInfoData => {
                                    if (programInfoData.length)
                                        this.setProgramInfo(programInfoData[0]);
                                });
                        }
                    }
                })
                .catch(error => {
                    EventBus.$emit("setErrorState", true, error);
                });
            },
            setActive(item, subitem) {
                let localData = null;
                if(this.data.type!== 'program' && this.isAlternativeMode){
                    localData = _.cloneDeep(this.defaultState);
                }else
                    localData = _.cloneDeep(this.data);
                let widgetList = localData.levels || localData.widgetList;
                widgetList.filter(el => el.id === item.id)[0].loaded = true;
                if (subitem) {
                    if (!subitem.active) {
                        widgetList.filter(el => el.active).forEach(el => (el.active = false));
                        widgetList.filter(el => el.id === item.id)[0].submenu.filter(el => el.active).forEach(el => (el.active = false));
                        widgetList.filter(el => el.id === item.id)[0].active = true;
                        widgetList.filter(el => el.id === item.id)[0].submenu.filter(el => el.text === subitem.text)[0].active = true;
                        widgetList.filter(el => el.id === item.id)[0].link = subitem.link;
                        EventBus.$emit("updateSidebar", localData, true);
                        EventBus.$emit("updateLoadedWidgets", {
                            id: subitem.widgetType ? subitem.widgetType : subitem.id,
                            link: subitem.link,
                            active: true,
                            widgetType: subitem.id
                        });
                    } else {
                        if (!item.active) {
                            widgetList.filter(el => el.active).forEach(el => (el.active = false));
                            widgetList.filter(el => el.id === item.id)[0].active = true;
                            EventBus.$emit("updateSidebar", localData, true);
                            EventBus.$emit("updateLoadedWidgets", {
                                id: item.widgetType ? item.widgetType : item.id,
                                link: item.link,
                                active: true,
                                widgetType: item.id
                            });
                        }
                    }
                } else {
                    if (!item.active && !item.submenu) {
                        widgetList.filter(el => el.active).forEach(el => (el.active = false));
                        widgetList.filter(el => el.id === item.id)[0].active = true;
                        EventBus.$emit("updateSidebar", localData, true);
                        let widgetId = item.widgetType;
                        if ('levels' in localData || !item.widgetType)
                            switch (item.id) {
                                case 'aerial':
                                case 'general':
                                case 'pedestrian':
                                case 'ground-plan':
                                    widgetId = 'digital-model';
                                break;
                                default:
                                    widgetId = item.id;
                                break;
                            }
                        EventBus.$emit("updateLoadedWidgets", {
                            id: widgetId,
                            link: item.link,
                            active: true,
                            widgetType: item.id
                        });
                    }
                }
            },
            setActiveHotspot(widget, hotspot) {
                let localData = _.cloneDeep(this.data);
                let widgetList = 'levels' in localData ? localData.levels : localData.widgetList;
                this.resetActiveHotspots(widgetList);

                let currentWidget = widgetList.filter(el => el.id === widget.id)[0];
                currentWidget.hotspots.forEach(el => el.active = false);
                currentWidget.active = true;
                let hotspotWidget = currentWidget.hotspots.filter(el => el.id === hotspot.id);
                if (hotspotWidget.length) {
                    hotspotWidget[0].active = true;
                    localData.activeHotspot = hotspot;
                }
                let hotspotWidgetType = hotspot.linkType || hotspot.type;
                EventBus.$emit("updateSidebar", localData, true);

                EventBus.$emit("updateLoadedWidgets", {
                    id: hotspotWidgetType,
                    link: hotspot.link,
                    active: true,
                    widgetType: hotspotWidgetType
                });
            },
            disableContextMenu(event) {
                if (process.env.NODE_ENV !== 'development')
                    event.preventDefault();
            },
            getFirstGeneralViewPreview(link) {
                if (link)
                    Http.getData(link).then(payload => {
                        if (payload) {
                            if (payload.images.length)
                                this.setLoadingBackground(payload.images[0][0])
                        }
                    }).catch(error => {
                        EventBus.$emit("setErrorState", true, error);
                    });
            },
            updateMention() {
                let isNonContractual = false;
                if (this.$props.data.type) {
                    let widgetId = null;
                    if (this.startedWidget === 'program' && this.$props.data.type === 'program') {
                        let activatedWidget = this.$props.data.widgetList.filter(item => {
                            return item.active;
                        });
                        widgetId = activatedWidget.length ? activatedWidget[0].id : null;
                    }
                    widgetId = widgetId === null ? this.$props.data.widgetType !== undefined && this.$props.data.widgetType !== null ? this.$props.data.widgetType : this.$props.data.type : widgetId;
                    isNonContractual = this.nonContractualWidgets.includes(this.$props.data.type);
                    this.mentionColor = this.darkWidget.includes(widgetId) ? this.themeColor.light : this.themeColor.default;
                    if (isNonContractual)
                        this.mentionMessage = ('disclaimer' in this.$props.data.gui && this.$props.data.gui.disclaimer) ? this.$props.data.gui.disclaimer.text : this.$t('mention.non-contractualVisual');
                    else {
                        this.mentionMessage = null;
                    }
                } else {
                    this.mentionMessage = null;
                }
            }
        },
        watch: {
            data() {
                this.updateMention();
            },
            'data.type': function() {
                this.updateMention();
            },
            mentionMessage(val) {
                this.isMentionVisible = false;
                if (val)
                    this.isMentionVisible = true;
            },
            snackbar() {
                if (this.snackbar)
                    this.snackbarModel = true;
            },
            snackbarModel() {
                if (!this.snackbarModel)
                    this.setSnackbar(null);
            }
        }
    }
</script>

<style lang="scss" scoped>
    .all-widgets {
        height: 100%;
        & div:not(.mention) {
            height: 100%;
        }
    }
</style>


// WEBPACK FOOTER //
// src/components/AllWidgets.vue